import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Phone, Building2, Trash2, ArrowLeft, RefreshCw, Inbox } from "lucide-react";
import { getLeads, getLeadStats, updateLeadStatus, deleteLead } from "@/lib/api";

const STATUSES = ["new", "contacted", "won", "lost"];
const STATUS_COLOR = {
  new: "text-accent border-accent",
  contacted: "text-blue-400 border-blue-400",
  won: "text-green-400 border-green-400",
  lost: "text-text-2 border-line",
};

export default function Admin() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, won: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([getLeads(), getLeadStats()]);
      setLeads(l);
      setStats(s);
    } catch {
      toast.error("Failed to load leads.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const changeStatus = async (id, status) => {
    try {
      await updateLeadStatus(id, status);
      setLeads((prev) => prev.map((x) => (x.id === id ? { ...x, status } : x)));
      const s = await getLeadStats();
      setStats(s);
    } catch {
      toast.error("Could not update status.");
    }
  };

  const remove = async (id) => {
    try {
      await deleteLead(id);
      setLeads((prev) => prev.filter((x) => x.id !== id));
      const s = await getLeadStats();
      setStats(s);
      toast.success("Lead deleted.");
    } catch {
      toast.error("Could not delete lead.");
    }
  };

  const filtered = filter === "all" ? leads : leads.filter((l) => l.status === filter);

  const STAT_CARDS = [
    { label: "Total Leads", value: stats.total },
    { label: "New", value: stats.new },
    { label: "Contacted", value: stats.contacted },
    { label: "Won", value: stats.won },
  ];

  return (
    <div className="min-h-screen bg-ink text-white font-body">
      <header className="border-b border-line">
        <div className="container-x flex items-center justify-between h-20">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 text-text-2 hover:text-accent transition-colors" data-testid="admin-back">
              <ArrowLeft className="w-4 h-4" /> <span className="mono-label">Site</span>
            </Link>
            <span className="w-px h-6 bg-line" />
            <h1 className="font-head font-extrabold tracking-tighter text-xl">Leads Dashboard</h1>
          </div>
          <button onClick={load} className="flex items-center gap-2 mono-label text-text-2 hover:text-accent transition-colors" data-testid="admin-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </header>

      <main className="container-x py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-line border border-line mb-10">
          {STAT_CARDS.map((c) => (
            <div key={c.label} className="bg-surface p-6" data-testid={`stat-${c.label.toLowerCase().replace(" ", "-")}`}>
              <div className="font-head font-black text-4xl">{c.value}</div>
              <div className="mono-label text-text-2 mt-1">{c.label}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {["all", ...STATUSES].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`filter-${f}`}
              className={`px-4 py-2 text-sm border capitalize transition-colors ${
                filter === f ? "border-accent text-accent bg-accent/10" : "border-line text-text-2 hover:border-white/30"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-text-2 py-20 text-center mono-label">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 border border-line bg-surface" data-testid="admin-empty">
            <Inbox className="w-10 h-10 text-line mb-4" />
            <p className="text-text-2">No leads yet. Submissions from the site appear here.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="leads-list">
            {filtered.map((lead) => (
              <div key={lead.id} data-testid={`lead-${lead.id}`} className="bg-surface border border-line p-6 grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                <div className="lg:col-span-3">
                  <div className="font-head font-bold text-lg">{lead.name}</div>
                  <div className="flex flex-col gap-1.5 mt-2 text-sm text-text-2">
                    <a href={`mailto:${lead.email}`} className="flex items-center gap-2 hover:text-accent"><Mail className="w-3.5 h-3.5" /> {lead.email}</a>
                    {lead.phone && <a href={`tel:${lead.phone}`} className="flex items-center gap-2 hover:text-accent"><Phone className="w-3.5 h-3.5" /> {lead.phone}</a>}
                    {lead.company && <span className="flex items-center gap-2"><Building2 className="w-3.5 h-3.5" /> {lead.company}</span>}
                  </div>
                </div>

                <div className="lg:col-span-5">
                  <div className="flex flex-wrap gap-2 mb-2">
                    <span className="mono-label text-accent border border-line px-2 py-1">{lead.service}</span>
                    {lead.budget && <span className="mono-label text-text-2 border border-line px-2 py-1">{lead.budget}</span>}
                  </div>
                  <p className="text-sm text-white/80 leading-relaxed">{lead.message}</p>
                  <div className="mono-label text-text-2 mt-3 !tracking-normal !normal-case !text-[0.65rem]">
                    {new Date(lead.created_at).toLocaleString()}
                  </div>
                </div>

                <div className="lg:col-span-4 flex flex-wrap items-center gap-2 lg:justify-end">
                  {STATUSES.map((s) => (
                    <button
                      key={s}
                      onClick={() => changeStatus(lead.id, s)}
                      data-testid={`set-status-${s}-${lead.id}`}
                      className={`px-3 py-1.5 text-xs border capitalize transition-colors ${
                        lead.status === s ? `${STATUS_COLOR[s]} bg-white/5` : "border-line text-text-2 hover:border-white/30"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                  <button onClick={() => remove(lead.id)} data-testid={`delete-${lead.id}`} className="w-8 h-8 flex items-center justify-center border border-line text-text-2 hover:border-red-500 hover:text-red-500 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
